import { useEffect, useRef, useState } from "react";
import API_BASE_URL from "../config";
import { isTerminalRunEvent } from "../utils/runEvents";

/**
 * Follows one agent run's live events over SSE and hands each one to `onEvent`.
 * The browser's EventSource reconnects on its own and sends `Last-Event-ID`, which the
 * server treats as the cursor — nothing is replayed twice or lost. The stream closes on
 * the run's terminal event; if it fails hard (`live` false) the caller's poll stays the
 * safety net.
 */
export default function useRunEvents(runId, { enabled = true, onEvent } = {}) {
  const [live, setLive] = useState(false);
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled || !runId) return undefined;
    if (typeof window === "undefined" || !("EventSource" in window)) return undefined;
    const es = new EventSource(`${API_BASE_URL}/api/v1/agents/runs/${encodeURIComponent(runId)}/events`,
      { withCredentials: true });

    es.onopen = () => setLive(true);
    es.onmessage = (e) => {
      let event;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      if (event.type === "heartbeat") return;
      if (handler.current) handler.current(event);
      if (isTerminalRunEvent(event)) {
        es.close();
        setLive(false);
      }
    };
    es.onerror = () => {
      // CONNECTING = the browser is already retrying with Last-Event-ID; CLOSED = give up.
      if (es.readyState === EventSource.CLOSED) setLive(false);
    };
    return () => {
      es.close();
      setLive(false);
    };
  }, [runId, enabled]);

  return { live };
}
